import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { REFERRAL_DATA } from '../../../assets/constants/referralData';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { authApi } from '../../../assets/api';
import { useTelegram } from '../../../assets/hooks/useTelegram';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import './Referral.css';

function getReferralMessageId(payload) {
  return payload?.message_id;
}

function parseTelegramVersion(version) {
  const [major = 0, minor = 0] = String(version || '')
    .split('.')
    .map((part) => Number.parseInt(part, 10));

  return {
    major: Number.isFinite(major) ? major : 0,
    minor: Number.isFinite(minor) ? minor : 0,
  };
}

function isTelegramVersionAtLeast(version, targetMajor, targetMinor = 0) {
  const { major, minor } = parseTelegramVersion(version);

  return major > targetMajor || (major === targetMajor && minor >= targetMinor);
}

function isPreparedMessageShareSupported(webApp) {
  if (!webApp) {
    return true;
  }

  if (typeof webApp.isVersionAtLeast === 'function') {
    return webApp.isVersionAtLeast('8.0');
  }

  return isTelegramVersionAtLeast(webApp.version, 8, 0);
}

function isTelegramShareCancelled(error) {
  const message = String(error?.message || '').toLowerCase();

  return message.includes('cancel')
    || message.includes('decline')
    || message.includes('denied');
}

function getTelegramWindow() {
  return typeof window === 'undefined' ? null : window;
}

function postTelegramEvent(eventType, eventData) {
  const win = getTelegramWindow();

  if (!win) {
    return false;
  }

  if (typeof win.Telegram?.WebView?.postEvent === 'function') {
    win.Telegram.WebView.postEvent(eventType, false, eventData);
    return true;
  }

  if (typeof win.TelegramWebviewProxy?.postEvent === 'function') {
    win.TelegramWebviewProxy.postEvent(eventType, JSON.stringify(eventData));
    return true;
  }

  if (typeof win.external?.notify === 'function') {
    win.external.notify(JSON.stringify({ eventType, eventData }));
    return true;
  }

  if (win.parent && win.parent !== win && typeof win.parent.postMessage === 'function') {
    win.parent.postMessage(JSON.stringify({ eventType, eventData }), '*');
    return true;
  }

  return false;
}

function buildTelegramShareUrl(referralLink) {
  const shareUrl = new URL('https://t.me/share/url');
  shareUrl.searchParams.set('url', referralLink);
  return shareUrl.toString();
}

function openTelegramShareLink(referralLink) {
  const win = getTelegramWindow();
  const shareUrl = buildTelegramShareUrl(referralLink);

  try {
    if (typeof win?.Telegram?.WebApp?.openTelegramLink === 'function') {
      win.Telegram.WebApp.openTelegramLink(shareUrl);
      return true;
    }
  } catch {
    // Fall through to platform event.
  }

  try {
    const url = new URL(shareUrl);
    if (postTelegramEvent('web_app_open_tg_link', {
      path_full: `${url.pathname}${url.search}`,
    })) {
      return true;
    }
  } catch {
    // Fall through to location fallback.
  }

  if (win?.location) {
    win.location.href = shareUrl;
    return true;
  }

  return false;
}

function waitPreparedMessageResult(timeoutMs = 45000) {
  const webView = getTelegramWindow()?.Telegram?.WebView;

  if (
    typeof webView?.onEvent !== 'function'
    || typeof webView?.offEvent !== 'function'
  ) {
    return Promise.resolve(true);
  }

  return new Promise((resolve, reject) => {
    let timeoutId;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      webView.offEvent('prepared_message_sent', handleSent);
      webView.offEvent('prepared_message_failed', handleFailed);
    };

    const handleSent = () => {
      cleanup();
      resolve(true);
    };

    const handleFailed = (eventType, eventData = {}) => {
      cleanup();
      const error = new Error(eventData.error || 'Share failed');
      error.reason = eventData.error;
      reject(error);
    };

    timeoutId = window.setTimeout(() => {
      cleanup();
      resolve(true);
    }, timeoutMs);

    webView.onEvent('prepared_message_sent', handleSent);
    webView.onEvent('prepared_message_failed', handleFailed);
  });
}

async function sendPreparedMessage(messageId) {
  const resultPromise = waitPreparedMessageResult();
  const isPosted = postTelegramEvent('web_app_send_prepared_message', {
    id: String(messageId),
  });

  if (!isPosted) {
    throw new Error('Telegram native share is unavailable');
  }

  return resultPromise;
}

async function copyToClipboard(value) {
  const text = String(value ?? '');

  if (!text) {
    return false;
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall back to the legacy path below.
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  textArea.style.pointerEvents = 'none';
  document.body.appendChild(textArea);
  textArea.select();

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textArea);
  }
}

function Referral() {
  const { currentUser } = useContext(CurrentUserContext);
  const { common, referral } = useContext(TranslationContext);
  const { webApp } = useTelegram();
  const [isSharingTelegram, setIsSharingTelegram] = useState(false);
  const [isCopyingLink, setIsCopyingLink] = useState(false);
  const referralAppLink = currentUser?.referral_info?.app_link || REFERRAL_DATA.link;
  const referralBotLink = currentUser?.referral_info?.bot_link || referralAppLink;
  const referralsCount = Number(currentUser?.stats?.referrals || 0);

  const copyReferralLink = async () => {
    const isCopied = await copyToClipboard(referralBotLink);

    if (!isCopied) {
      toast.error(common.failedToCopy);
      return false;
    }

    toast(common.linkCopied, {
      autoClose: 1600,
      className: 'app-toast app-toast--copy',
    });
    return true;
  };

  const handleShareInTelegram = async () => {
    if (isSharingTelegram) return;
    triggerImpactHaptic('light');
    setIsSharingTelegram(true);

    try {
      if (!isPreparedMessageShareSupported(webApp)) {
        if (openTelegramShareLink(referralAppLink)) {
          return;
        }

        await copyReferralLink();
        return;
      }

      const payload = await authApi.sendReferralMessage();
      const messageId = getReferralMessageId(payload);

      if (!messageId) {
        throw new Error(referral.missingMessageId);
      }

      await sendPreparedMessage(messageId);

      toast(referral.inviteShared, {
        autoClose: 1600,
        className: 'app-toast app-toast--copy',
      });
    } catch (error) {
      if (isTelegramShareCancelled(error)) {
        return;
      }

      if (openTelegramShareLink(referralAppLink)) {
        return;
      }

      try {
        await copyReferralLink();
      } catch {
        toast.error(referral.shareFailed);
      }
    } finally {
      setIsSharingTelegram(false);
    }
  };

  const handleCopyLink = async () => {
    if (isCopyingLink) return;
    triggerImpactHaptic('light');
    setIsCopyingLink(true);

    try {
      await copyReferralLink();
    } finally {
      setIsCopyingLink(false);
    }
  };

  return (
    <section className="referral">
      <div className="referral__content">
        <header className="referral__header">
          <h1 className="referral__title">{referral.title}</h1>
        </header>

        <section className="referral__hero">
          <div className="referral__hero-card">
            <h2 className="referral__hero-title">{referral.heroTitle}</h2>
            <p className="referral__hero-text">{referral.heroText}</p>
            <p className="referral__hero-note">{referral.heroNote}</p>
          </div>
        </section>

        <div className="referral__stats referral__stats--single">
          <div className="referral__stat-card">
            <span className="referral__stat-value">{referralsCount}</span>
            <span className="referral__stat-label">{referral.friendsJoined}</span>
          </div>
        </div>

        <div className="referral__actions">
          <button
            type="button"
            className="referral__share referral__share--secondary"
            onClick={handleCopyLink}
            disabled={isCopyingLink}
          >
            {isCopyingLink ? referral.copying : referral.copyLink}
          </button>

          <button
            type="button"
            className="referral__share referral__share--primary"
            onClick={handleShareInTelegram}
            disabled={isSharingTelegram}
          >
            {isSharingTelegram ? referral.sharePreparing : common.share}
          </button>
        </div>
      </div>

    </section>
  );
}

export default Referral;
